import React, { useState } from 'react';
import { useMyLeaveBalanceSummary } from '../../hooks/useLeaveBalance';
import { useMyLeaveRequests } from '../../hooks/useLeaveRequests';
import { LeaveRequestStatus } from '../../types';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import LeaveRequestFormModal from '../../components/leave-requests/LeaveRequestFormModal';
import './LeaveBalanceDashboard.css';

const LeaveBalanceDashboard: React.FC = () => {
  const { summary, loading: loadingSummary, error: errorSummary, refetch } = useMyLeaveBalanceSummary();
  const { requests: upcomingRequests, loading: loadingRequests, refetch: refetchRequests } = useMyLeaveRequests(
    LeaveRequestStatus.APPROVED
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  if (loadingSummary || loadingRequests) {
    return <Loading text="Loading your leave balance..." />;
  }

  if (errorSummary) {
    return <ErrorMessage error={errorSummary} onRetry={refetch} />;
  }

  if (!summary) {
    return <ErrorMessage error="No balance data available" />;
  }

  // Filter upcoming leaves (future dates only)
  const upcoming = upcomingRequests.filter(
    (req) => new Date(req.startDate) >= new Date()
  );

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    refetch();
    refetchRequests();
  };

  return (
    <div className="leave-balance-dashboard">
      <div className="dashboard-header">
        <h1>Leave Balance Overview</h1>
        <button
          className="button button-primary create-request-button"
          onClick={() => setIsCreateModalOpen(true)}
        >
          + Create Leave Request
        </button>
      </div>

      <div className="balance-cards">
        <div className="balance-card balance-card-primary">
          <div className="card-icon">💼</div>
          <div className="card-content">
            <h3>Remaining Balance</h3>
            <p className="card-value">{summary.remainingDays}</p>
            <p className="card-label">days available</p>
          </div>
        </div>

        <div className="balance-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <h3>Accrued This Year</h3>
            <p className="card-value">{summary.accruedDays}</p>
            <p className="card-label">days earned</p>
          </div>
        </div>

        <div className="balance-card">
          <div className="card-icon">✓</div>
          <div className="card-content">
            <h3>Used</h3>
            <p className="card-value">{summary.usedDays}</p>
            <p className="card-label">days taken</p>
          </div>
        </div>
      </div>

      <div className="balance-history">
        <h2>Balance History</h2>
        <table className="history-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Accrued</th>
              <th>Used</th>
            </tr>
          </thead>
          <tbody>
            <tr className="current-year">
              <td>{summary.currentYear} (Current)</td>
              <td>{summary.accruedDays} days</td>
              <td>{summary.usedDays} days</td>
            </tr>
            {summary.previousYears.map((year) => (
              <tr key={year.year}>
                <td>{year.year}</td>
                <td>{year.accruedDays} days</td>
                <td>{year.usedDays} days</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {upcoming.length > 0 && (
        <div className="upcoming-leaves">
          <h2>Upcoming Approved Leaves</h2>
          <div className="upcoming-list">
            {upcoming.slice(0, 5).map((request) => (
              <div key={request.id} className="upcoming-item">
                <div className="upcoming-dates">
                  {new Date(request.startDate).toLocaleDateString('en-GB')} -
                  {new Date(request.endDate).toLocaleDateString('en-GB')}
                </div>
                <div className="upcoming-meta">
                  <span className="upcoming-type">{request.leaveType?.name}</span>
                  <span className="upcoming-days">{request.totalDays} days</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Leave Request"
        size="large"
      >
        <LeaveRequestFormModal
          onSuccess={handleCreateSuccess}
          onCancel={() => setIsCreateModalOpen(false)}
          hideBalanceInfo={true}
        />
      </Modal>
    </div>
  );
};

export default LeaveBalanceDashboard;